import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { Typography, TextField } from "@material-ui/core";
import { useFormik } from "formik";

import { useStore } from "@store/store";
import Header from "@components/header";
import ProfileSideBar from "@components/profile-sidebar";
import DeletePopup from "@components/deletePopup";
import SearchIcon from "@assets/images/search.svg";
import {
  FETCH_CONNECTED_CUSTOMER_USERS,
  FETCH_CONNECTED_CUSTOMER_USERS_SUCCESS,
  FETCH_CONNECTED_CUSTOMER_USERS_FAILURE,
  ADD_CONNECTED_CUSTOMER_USER,
  ADD_CONNECTED_CUSTOMER_USER_SUCCESS,
  ADD_CONNECTED_CUSTOMER_USER_FAILURE,
  EDIT_CONNECTED_CUSTOMER_USER,
  EDIT_CONNECTED_CUSTOMER_USER_SUCCESS,
  EDIT_CONNECTED_CUSTOMER_USER_FAILURE,
  DELETE_CONNECTED_CUSTOMER_USER,
  DELETE_CONNECTED_CUSTOMER_USER_SUCCESS,
  DELETE_CONNECTED_CUSTOMER_USER_FAILURE,
  GET_PARENT_CUSTOMER,
  GET_PARENT_CUSTOMER_SUCCESS,
  GET_PARENT_CUSTOMER_FAILURE,
} from "@utils/actionTypes";
import { schema } from "@utils/schemas";
import { rowsPerPageVal } from "@utils/constant";
import validationSchema from "@utils/validationSchemas";
import API from "@services/axios";
import AddUser from "./add-connected-customer-user";
import TableListing from "./table-listing";
import { ConnectedCustomerUsersStyle } from "./style";
import { useDebouncedEffect } from "@hooks/debounceEffect";

function ConnectedCustomerUsers() {
  const classes = ConnectedCustomerUsersStyle();
  const [openDeletePopup, setDeletePopup] = useState(false);
  const [getDeleteUser, setDeleteUser] = useState({});
  const [openAddPopup, setOpenAddPopup] = useState(false);
  const [getEditUser, setEditUser] = useState(
    schema.addConnectedCustomerSchema
  );
  const [getEdit, setEdit] = useState(false);
  const [debounceSearch, setDebounceSearch] = useState("");
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageVal);
  const [state, dispatch] = useStore();
  const [err, setError] = useState("");
  const [search, setSearch] = useState("");
  const isFirstRender = useRef(true);

  // API calling to get list of connected customers
  let getUsers = () => {
    const params = {
      page: page + 1,
      size: rowsPerPage,
      ...(!!order ? { order } : {}),
      ...(!!orderBy ? { orderBy } : {}),
      ...(!!search ? { search } : {}),
    };
    dispatch({ type: FETCH_CONNECTED_CUSTOMER_USERS });
    API.get("connectedCustomer", { params })
      .then((response) => {
        dispatch({
          type: FETCH_CONNECTED_CUSTOMER_USERS_SUCCESS,
          payload: response.data.data,
        });
      })
      .catch((err) => {
        dispatch({
          type: FETCH_CONNECTED_CUSTOMER_USERS_FAILURE,
          payload: err,
        });
      });
  };

  // API calling to add connected customers
  let addUser = (value) => {
    const data = {
      name: value.name,
      email: value.email,
      password: value.password,
      phone: value.phone,
      customerParentIds: value.parentCustomer,
    };
    dispatch({ type: ADD_CONNECTED_CUSTOMER_USER });
    API.post("connectedCustomer", data)
      .then((response) => {
        handleCloseAddPopup();
        getUsers();
        toast.success("Connected Customer User Added Successfully");
        dispatch({
          type: ADD_CONNECTED_CUSTOMER_USER_SUCCESS,
          payload: response.data.data,
        });
      })
      .catch((error) => {
        if (error.response.data.code === 409)
          setError(error.response?.data?.message);
        dispatch({ type: ADD_CONNECTED_CUSTOMER_USER_FAILURE, payload: error });
      });
  };

  // API calling to edit connected customers
  let editUser = (value) => {
    let data = {
      name: value.name,
      email: value.email,
      phone: value.phone,
      customerParentIds: value.parentCustomer,
    };
    data = {
      ...data,
      ...(!!value.password ? { password: value.password } : {}),
    };
    dispatch({ type: EDIT_CONNECTED_CUSTOMER_USER });
    API.put(`connectedCustomer/${value.id}`, data)
      .then((response) => {
        handleCloseAddPopup();
        getUsers();
        toast.success("Connected Customer User Updated Successfully");
        dispatch({
          type: EDIT_CONNECTED_CUSTOMER_USER_SUCCESS,
          payload: response.data.data,
        });
      })
      .catch((error) => {
        if (error.response.data.code === 409)
          setError(error.response?.data?.message);
        dispatch({
          type: EDIT_CONNECTED_CUSTOMER_USER_FAILURE,
          payload: error,
        });
      });
  };

  useEffect(() => {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, order, orderBy]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPage(0);
    if (page === 0) {
      getUsers();
    }
  }, [debounceSearch]);

  useEffect(() => {
    dispatch({ type: GET_PARENT_CUSTOMER });
    API.get("master/parentCustomers")
      .then((response) => {
        dispatch({
          type: GET_PARENT_CUSTOMER_SUCCESS,
          payload: response.data.data,
        });
      })
      .catch((err) => {
        dispatch({ type: GET_PARENT_CUSTOMER_FAILURE, payload: err });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAddPopup]);

  // API calling to delete connected customers
  const handleDelete = () => {
    dispatch({ type: DELETE_CONNECTED_CUSTOMER_USER });
    API.delete(`connectedCustomer/${getDeleteUser.id}`)
      .then((response) => {
        setDeletePopup(false);
        getUsers();
        toast.success("Connected Customer User Deleted Successfully");
        dispatch({
          type: DELETE_CONNECTED_CUSTOMER_USER_SUCCESS,
          payload: response.data.data,
        });
      })
      .catch((error) => {
        setError("You can't delete this record");
        dispatch({
          type: DELETE_CONNECTED_CUSTOMER_USER_FAILURE,
          payload: error,
        });
      });
  };

  const handleDeletePopup = (user) => {
    setDeleteUser(user);
    setDeletePopup(true);
    setError("");
  };

  const handleAddPopup = (user) => {
    if (user) {
      const data = {
        id: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        password: "",
        confirmPassword: "",
        parentCustomer: JSON.parse(user.customerParentIds),
      };
      setEdit(true);
      // eslint-disable-next-line
      setEditUser(data);
    }
    setOpenAddPopup(true);
  };
  const handleCloseAddPopup = () => {
    setOpenAddPopup(false);
    setEdit(false);
    setEditUser(schema.addConnectedCustomerSchema);
    setError("");
    formik.handleReset();
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getEditUser,
    validationSchema: getEdit
      ? validationSchema.editConnectedCustomerValidationSchema
      : validationSchema.addConnectedCustomerValidationSchema,
    onSubmit: (value) => {
      getEdit ? editUser(value) : addUser(value);
    },
  });

  const handleSorting = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  useDebouncedEffect(
    () => {
      setDebounceSearch(search);
    },
    1000,
    [search]
  );

  const handleSearch = (event) => {
    setSearch(event.target.value.trimStart());
  };

  return (
    <>
      <Header />
      <div className={classes.CustomerListingWrapper}>
        <div className="setting-page wrapper">
          <div className="inner-page">
            <Typography variant="h1">Settings</Typography>
            <div className="setting-row-wrapper">
              <div className="left-sidebar">
                <ProfileSideBar />
              </div>
              <div className="right-content">
                <div className="white-card right-content-inner">
                  <div className={classes.innerPageTopBlock}>
                    <div className="left-block">
                      <Typography variant="h1">
                        Connected Customer Users
                      </Typography>
                    </div>
                    <div className="right-block">
                      <div className="right-block-inner">
                        <div className="search-wrapper">
                          <div className="form-gourp">
                            <TextField
                              id="search-request"
                              placeholder="Search Connected Customer Users"
                              variant="outlined"
                              type="search"
                              InputProps={{
                                endAdornment: (
                                  <img src={SearchIcon} alt="Search" />
                                ),
                              }}
                              onChange={handleSearch}
                              value={search}
                            />
                          </div>
                        </div>
                        <div className="modal-wrapper">
                          <div className="btn-wrapper">
                            <AddUser
                              handleClickOpen={() => handleAddPopup()}
                              handleClose={handleCloseAddPopup}
                              open={openAddPopup}
                              formik={formik}
                              isEdit={getEdit}
                              error={err}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <TableListing
                    handleOpen={(user) => handleDeletePopup(user)}
                    handleEdit={(editUser) => handleAddPopup(editUser)}
                    handleSorting={(e, property) => handleSorting(e, property)}
                    orderBy={orderBy}
                    order={order}
                    page={page}
                    handleChangePage={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                  />
                  <DeletePopup
                    open={openDeletePopup}
                    handleClose={() => setDeletePopup(false)}
                    handleDelete={(deleteUser) => handleDelete(deleteUser)}
                    deleteUser={getDeleteUser.name}
                    loading={state.connectedUser.deletingUser}
                    error={err}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default ConnectedCustomerUsers;
